import { QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getApiErrorMessage, IApiAxiosError } from "@/api";
import { ILibraryDragItem } from "@/components/shared/document-picker/DocumentPicker.types";
import { invalidateLibraryQueries } from "./invalidateLibraryQueries";
import { moveItemById } from "./moveItemById";

export interface MoveWithUndoOptions {
    /** Unit 8 (insurescope-7149k.3.2): a move inside a client workspace
     * must refresh the client surfaces too — after the move AND after its
     * undo, the invalidation set includes ["client", clientId],
     * ["clients"], and ["clients-stats"] in addition to the library keys
     * (header count, clients-list row, and stats header all hang off
     * them). Absent = the move didn't originate inside a client
     * workspace; the null arm carried no distinct semantics and was
     * dropped (insurescope-g5bqp, charge f). */
    clientId?: number;
}

/** The move/undo invalidation set (on settle, never optimistically — the
 * pin-polly optimistic-clobber-by-refetch learning). Unit 1 made every
 * placement change a potential retag wherever the move originates, so the
 * clients list and stats header are invalidated unconditionally (free for
 * inactive queries); the client detail header refreshes when the caller
 * knows which workspace it acted in. */
function invalidateMoveQueries(
    queryClient: QueryClient,
    options?: MoveWithUndoOptions
) {
    invalidateLibraryQueries(queryClient);
    queryClient.invalidateQueries({ queryKey: ["clients"] });
    queryClient.invalidateQueries({ queryKey: ["clients-stats"] });
    if (options?.clientId != null) {
        queryClient.invalidateQueries({
            queryKey: ["client", options.clientId],
        });
    }
}

export async function executeMoveWithUndo(
    items: ILibraryDragItem[],
    targetFolderId: number | null,
    targetFolderName: string,
    queryClient: QueryClient,
    onComplete?: () => void,
    options?: MoveWithUndoOptions
): Promise<void> {
    const results = await Promise.allSettled(
        items.map((item) =>
            moveItemById(item.type, item.numericId, targetFolderId)
        )
    );

    const failures = results.filter(
        (r): r is PromiseRejectedResult => r.status === "rejected"
    );

    if (failures.length === items.length) {
        // Surface a backend guard's specific detail (e.g. Unit 1's
        // "Can't move a client's root folder") over the generic copy.
        toast.error(
            getApiErrorMessage(
                failures[0].reason as IApiAxiosError,
                "Failed to move items"
            )
        );
        return;
    }

    invalidateMoveQueries(queryClient, options);
    onComplete?.();

    const movedCount = items.length - failures.length;
    const label =
        movedCount === 1 && items.length === 1
            ? `Moved "${items[0].name}" to ${targetFolderName}`
            : `Moved ${movedCount} item${movedCount > 1 ? "s" : ""} to ${targetFolderName}`;

    const undoItems = items.filter((_, i) => results[i].status === "fulfilled");
    const toastId = toast.success(label, {
        duration: 6000,
        action: {
            label: "Undo",
            onClick: async () => {
                toast.dismiss(toastId);
                const undoResults = await Promise.allSettled(
                    undoItems.map((item) => {
                        const originalFolderId =
                            item.type === "folder"
                                ? (item.parentId ?? null)
                                : item.libraryFolderId;
                        return moveItemById(
                            item.type,
                            item.numericId,
                            originalFolderId
                        );
                    })
                );
                invalidateMoveQueries(queryClient, options);
                const undoFailures = undoResults.filter(
                    (r) => r.status === "rejected"
                );
                if (undoFailures.length === undoItems.length) {
                    toast.error("Failed to undo move");
                } else if (undoFailures.length > 0) {
                    toast.warning("Some items could not be reverted");
                } else {
                    toast.info("Move undone");
                }
            },
        },
    });

    if (failures.length > 0) {
        toast.warning(
            `${failures.length} item${failures.length > 1 ? "s" : ""} failed to move`
        );
    }
}
